import Tabs from '../components/tabs/tabs';
import SpecialtyFilterTable from '../components/specialty-filter-table';
import ComplianceScore from '../components/compliance-score';
import NetworkMap from '../components/network-map';
import './networkAnalysisPage.scss';
import AdditionalProvidersTable from '../components/additional-providers-table';
import {Button, Layer, Modal, TextInput, TextArea, InlineNotification, SkeletonPlaceholder, ToastNotification} from '@carbon/react';
import ComplianceSummaryTable from '../components/summary';
import {observer} from 'mobx-react';
import {useContext, useEffect, useState, useCallback, useMemo} from 'react';
import {useParams, Navigate} from 'react-router-dom';
import {NetworkContext} from '../lib/context';
import {ResetAlt} from '@carbon/icons-react';
import Spinner from '../../../components/spinner';
import NetworkAnalysisGeographyFilter from '../components/no-filters/network-analysis-geography-filter';
import DisruptionAnalysisAdditionalProviderTable from '../components/disruption-analysis-additional-provider-table';
import DisruptionAnalysisNetworkMap from '../components/disruption-analysis-network-map';
import MultiWayToggle from '../components/multi-way-toggle';
import DisruptionAnalysisSpecialtySummaryTable from '../components/specialty_summary_disruption_analysis';
import RevisionIdSelector from '../components/revision-id-selector';
import {SHOW_DISRUPTION_ANALYSIS_PAGES} from '../../../lib/api';
import LoadingModal from '../components/loading-modal';
import handleError from '../../../lib/error';
import DisruptionAnalysisFilter from '../components/no-filters/disruption-analysis-filter';
import Export from '../pages/reports-page/components/Export';

const PAGE_HEIGHT = 'min-h-[calc(100vh-11rem)]';
const DOWNLOAD_COMPLIANCE_SUMMARY_ITEM = 'complianceSummary';

// Extract ExploreView as a separate component to prevent re-creation
const ExploreView = observer(({ 
    handleSetSaveRosterModalOpen,
    specialtyLoading,
    specialtyError,
    mapDataLoading,
    mapDataError
}: { 
    handleSetSaveRosterModalOpen: (open: boolean) => void,
    specialtyLoading?: boolean,
    specialtyError?: string | null,
    mapDataLoading?: boolean,
    mapDataError?: string | null
}) => {
    return <div className="bg-background-gray-100 relative">
        <div
            className={`network-analysis-page grid grid-cols-9 grid-rows-9 gap-8 ${PAGE_HEIGHT} p-4 pl-12 mr-[calc(16rem+6rem)]`}>
            <div className="col-span-2 row-span-4">
                <ComplianceScore 
                    compactView
                    specialtyLoading={specialtyLoading}
                    specialtyError={specialtyError}
                />
            </div>
            <div className="col-span-7 row-span-4">
                <NetworkMap setSaveRosterModalOpen={handleSetSaveRosterModalOpen} mapDataLoading={mapDataLoading} mapDataError={mapDataError} />
            </div>
            <div className="col-span-9 row-span-5">
                <Layer className="h-full">
                    <AdditionalProvidersTable setSaveRosterModalOpen={handleSetSaveRosterModalOpen} />
                </Layer>
            </div>
        </div>
        <div className="network-analysis-page absolute right-0 top-0 min-h-[calc(100%)]">
            <NetworkAnalysisGeographyFilter />
        </div>
    </div>;
});

const SummaryView = observer(({
    specialtyLoading,
    specialtyError,
    exportButton
}: {
    specialtyLoading?: boolean,
    specialtyError?: string | null,
    exportButton?: React.ReactNode
}) => {
    return <div className="bg-background-gray-100 relative">
        <div className={`network-analysis-page grid grid-cols-1 grid-rows-1 gap-8 ${PAGE_HEIGHT} p-4 pl-12 mr-[calc(16rem+6rem)]`}>
            <div className="col-span-1 h-full">
                <ComplianceSummaryTable
                    specialtyLoading={specialtyLoading}
                    specialtyError={specialtyError}
                    exportButton={exportButton}
                />
            </div>
        </div>
        <div className='network-analysis-page absolute right-0 top-0 h-[calc(100%)]'>
            <NetworkAnalysisGeographyFilter />
        </div>
    </div>;
});

const ResultsView = observer(({
    handleSetSaveRosterModalOpen,
    specialtyLoading,
    specialtyError,
    mapDataLoading,
    mapDataError
}: {
    handleSetSaveRosterModalOpen: (open: boolean) => void,
    specialtyLoading?: boolean,
    specialtyError?: string | null,
    mapDataLoading?: boolean,
    mapDataError?: string | null
}) => {
    return <div className='bg-background-gray-100 relative'>
        <div className={`network-analysis-page grid grid-cols-5 grid-rows-5 gap-8 ${PAGE_HEIGHT} p-4 pl-12 mr-[calc(16rem+6rem)]`}>
            <div className="row-span-5">
                <SpecialtyFilterTable
                    fullHeight
                    specialtyLoading={specialtyLoading}
                    specialtyError={specialtyError}
                />
            </div>
            <div className="col-span-4">
                <ComplianceScore
                    specialtyLoading={specialtyLoading}
                    specialtyError={specialtyError}
                />
            </div>
            <div className="row-span-4 col-span-4">
                <NetworkMap
                    setSaveRosterModalOpen={handleSetSaveRosterModalOpen}
                    mapDataLoading={mapDataLoading}
                    mapDataError={mapDataError}
                />
            </div>
        </div>
        <div className='network-analysis-page absolute right-0 top-0 h-[calc(100%)]'>
            <NetworkAnalysisGeographyFilter />
        </div>
    </div>;
});

const DisruptionAnalysis = observer(({ 
    handleSetSaveRosterModalOpen 
}: { 
    handleSetSaveRosterModalOpen: (open: boolean) => void 
}) => {
    const noStore = useContext(NetworkContext);
    const disruptionTabs = [
        {
            value: 'specialty-summary',
            label: 'Specialty Summary',
            content: <DisruptionAnalysis_SpecialtySummaryMapLayout />,
            ['data-testid']: 'tab-specialty-summary'
        },
        {
            value: 'network-map',
            label: 'Network Map',
            content: <DisruptionAnalysis_NetworkMapLayout handleSetSaveRosterModalOpen={handleSetSaveRosterModalOpen} />,
            ['data-testid']: 'tab-network-map'
        },
        {
            value: 'providers-table',
            label: 'Providers Table',
            content: <DisruptionAnalysis_ProviderTableLayout handleSetSaveRosterModalOpen={handleSetSaveRosterModalOpen} />,
            isDisabled: !noStore.disruptionAnalysisSpecialtyFilter,
            disabledTooltipText: 'Please select a specialty to view providers.',
            ['data-testid']: 'tab-providers-table'
        },
        {
            value: 'combined-view',
            label: 'Combined View',
            content: <DisruptionAnalysis_CombinedView handleSetSaveRosterModalOpen={handleSetSaveRosterModalOpen} />,
            ['data-testid']: 'tab-combined-view'
        }
    ];
    return (
        <div className="bg-background-gray-100 relative">
            <div className='network-analysis-page pt-4 pl-12 mr-[calc(16rem+6rem)]'>
                <MultiWayToggle
                    options={disruptionTabs}
                    alignment='right'
                />
            </div>
            <div className="network-analysis-page absolute right-0 top-0 h-[calc(100%)]">
                <DisruptionAnalysisFilter />
            </div>
        </div>
    );
});

const DisruptionAnalysis_SpecialtySummaryMapLayout = observer(() => {
    return <div className={`network-analysis-page grid grid-cols-9 grid-rows-9 gap-8 ${PAGE_HEIGHT} p-4 pr-0`}>
        <div className="col-span-9 row-span-8">
            <DisruptionAnalysisSpecialtySummaryTable/>
        </div>
    </div>;
});

const DisruptionAnalysis_NetworkMapLayout = observer(({ 
    handleSetSaveRosterModalOpen 
}: { 
    handleSetSaveRosterModalOpen: (open: boolean) => void 
}) => {
    return <div className={`network-analysis-page grid grid-cols-9 grid-rows-9 gap-8 ${PAGE_HEIGHT} p-4 pr-0`}>
        <div className="col-span-9 row-span-8">
            <DisruptionAnalysisNetworkMap setSaveRosterModalOpen={handleSetSaveRosterModalOpen} />
        </div>
    </div>;
});

const DisruptionAnalysis_ProviderTableLayout = observer(({ 
    handleSetSaveRosterModalOpen 
}: { 
    handleSetSaveRosterModalOpen: (open: boolean) => void 
}) => {
    return <div className={`network-analysis-page grid grid-cols-9 grid-rows-9 gap-8 ${PAGE_HEIGHT} p-4 pr-0`}>
        <div className="col-span-9 row-span-8">
            <Layer className="h-full">
                <DisruptionAnalysisAdditionalProviderTable setSaveRosterModalOpen={handleSetSaveRosterModalOpen} />
            </Layer>
        </div>
    </div>;
});

const DisruptionAnalysis_CombinedView = observer(({ 
    handleSetSaveRosterModalOpen 
}: { 
    handleSetSaveRosterModalOpen: (open: boolean) => void 
}) => {
    return <div className={`network-analysis-page grid grid-cols-9 grid-rows-9 gap-8 ${PAGE_HEIGHT} p-4 pr-0`}>
        <div className="col-span-9 row-span-4">
            <DisruptionAnalysisNetworkMap setSaveRosterModalOpen={handleSetSaveRosterModalOpen} />
        </div>
        <div className="col-span-9 row-span-4">
            <Layer className="h-full">
                <DisruptionAnalysisAdditionalProviderTable setSaveRosterModalOpen={handleSetSaveRosterModalOpen} />
            </Layer>
        </div>
    </div>;
});

enum NetworkAnalysisTabs {
    Summary = 0,
    Results = 1,
    Explore = 2,
    DisruptionAnalysis = 3
}

function NetworkAnalysisPage() {
    const networkOptimizationStore = useContext(NetworkContext);
    const {rosterId} = useParams();
    const [loading, setLoading] = useState<boolean>(false);
    const [saveRosterModalOpen, setSaveRosterModalOpen] = useState(false);
    const [fileName, setFileName] = useState('');
    const [note, setNote] = useState('');
    const [successNotification, setSuccessNotification] = useState(false);
    const [specialtyLoadingCount, setSpecialtyLoadingCount] = useState<number>(0);
    const [specialtyError, setSpecialtyError] = useState<string | null>(null);
    const specialtyLoading = useMemo(() => specialtyLoadingCount > 0, [specialtyLoadingCount]);
    const [mapDataLoadingCount, setMapDataLoadingCount] = useState<number>(0);
    const mapDataLoading = useMemo(() => mapDataLoadingCount > 0, [mapDataLoadingCount]);
    const [mapDataError, setMapDataError] = useState<string | null>(null);

    useEffect(() => {
        networkOptimizationStore.resetGeographyFilter();
    }, []);

    // Reset filters when switching between tabs to prevent filter conflicts
    useEffect(() => {
        if (networkOptimizationStore.activeTab === NetworkAnalysisTabs.DisruptionAnalysis) {
            // On Disruption Analysis tab, reset geography filters
            networkOptimizationStore.resetGeographyFilter();
        } else {
            // On other tabs (Summary, Results, Explore), reset disruption analysis filters
            networkOptimizationStore.resetDisruptionAnalysisFilter();
        }
    }, [networkOptimizationStore.activeTab]);

    // Update fileName when modal opens or roster name changes
    useEffect(() => {
        if (saveRosterModalOpen && networkOptimizationStore.rosterFileName) {
            setFileName(networkOptimizationStore.rosterFileName);
        }
    }, [saveRosterModalOpen, networkOptimizationStore.rosterFileName]);

    useEffect(() => {
        const controller = new AbortController();
        const loadData = async () => {
            try {
                setSpecialtyLoadingCount(count => count + 1);
                if (networkOptimizationStore.activeTab === NetworkAnalysisTabs.DisruptionAnalysis) {
                    await networkOptimizationStore.loadDisruptionAnalysisSpecialtiesData(controller);
                } else {
                    await networkOptimizationStore.loadSpecialtiesData(controller);
                }
            } catch (err) {
                console.error('Failed to load estimated compliance summary data. Please try again later.');
                setSpecialtyError('Failed to load specialties data. Please try again later.');
            } finally {
                setSpecialtyLoadingCount(count => count - 1);
            }
        };
        loadData();
        return () => controller.abort();
    }, [
        networkOptimizationStore.activeTab,
        networkOptimizationStore.appliedDisruptionAnalysisFilter.state,
        networkOptimizationStore.appliedDisruptionAnalysisFilter.standard_type,
        networkOptimizationStore.appliedDisruptionAnalysisFilter.patient_roster_name,
        networkOptimizationStore.runId,
        networkOptimizationStore.revisionId,
        networkOptimizationStore.readyToLoadData(),
        networkOptimizationStore.appliedGeographyFilter,
        networkOptimizationStore.distanceCalcType,
        networkOptimizationStore.rosterRunCount,
        networkOptimizationStore.loadingRevisionRecalculation
    ]);

    const shouldFetchMapData = useMemo(() => {
        return [NetworkAnalysisTabs.Results, NetworkAnalysisTabs.Explore].includes(networkOptimizationStore.activeTab);
    }, [networkOptimizationStore.activeTab]);

    useEffect(() => {
        if (shouldFetchMapData) {
            const controller = new AbortController();
            (async () => {
                try {
                    setMapDataLoadingCount(count => count + 1);
                    setMapDataError('');
                    await networkOptimizationStore.loadMapGeoJson(controller);
                } catch (error) {
                    handleError(error as Error, setMapDataError);
                } finally {
                    setMapDataLoadingCount(count => count - 1);
                }
            })();
            return () => controller.abort();
        }
    }, [
        shouldFetchMapData,
        networkOptimizationStore.appliedGeographyFilter,
        networkOptimizationStore.specialtyFilter,
        networkOptimizationStore.runId,
        networkOptimizationStore.revisionId,
        networkOptimizationStore.distanceCalcType,
        networkOptimizationStore.rosterRunCount
    ]);

    const handleSetSaveRosterModalOpen = useCallback((open: boolean) => {
        setSaveRosterModalOpen(open);
    }, []);

    const handleSaveNewRoster = useCallback(async () => {
        const controller = new AbortController();
        try {
            setSaveRosterModalOpen(false);
            const rosterIdNum = parseInt(rosterId || '0', 10);
            const result = await networkOptimizationStore.copyRoster(rosterIdNum, fileName, note, controller);
            
            if (result?.success && result.revisionId !== null) {
                // Switch to the newly created revision and update it with the changes
                networkOptimizationStore.setRevisionId(result.revisionId);
                await networkOptimizationStore.updateRoster(controller);
                setSuccessNotification(true);
            }
            
            setFileName('');
            setNote('');
        } catch (err) {
            console.error('Failed to save new roster. Please try again.');
        }
    }, [fileName, note, rosterId, networkOptimizationStore]);

    function revertToOriginal() {
        setLoading(true);
        networkOptimizationStore.revertProviderRosterToOriginal(new AbortController())
            .then(() => networkOptimizationStore.showRevertModal = false)
            .finally(() => setLoading(false));
    }

    const handleExportSummary = useCallback(() => {
        networkOptimizationStore.downloadComplianceSummary(DOWNLOAD_COMPLIANCE_SUMMARY_ITEM, new AbortController().signal);
    }, [networkOptimizationStore]);

    const summaryExportButton = useMemo(() => {
        // Enable export when:
        // 1. Roster is loaded (runId exists)
        // 2. Filters are properly set (readyToLoadData)
        // 3. There is data to export (complianceScores has data)
        // 4. Not currently loading specialty data
        const hasData = networkOptimizationStore.complianceScores.length > 0;
        const canExport = networkOptimizationStore.runId &&
                         networkOptimizationStore.readyToLoadData() &&
                         hasData &&
                         !specialtyLoading;
        const isExporting = networkOptimizationStore.downloadingItems[DOWNLOAD_COMPLIANCE_SUMMARY_ITEM];

        return (
            <Export
                isExporting={isExporting}
                menuItems={[
                    {
                        disabled: !canExport || isExporting,
                        label: 'Export as CSV',
                        onClick: handleExportSummary
                    }
                ]}
            />
        );
    }, [
        networkOptimizationStore.runId,
        networkOptimizationStore.revisionId,
        networkOptimizationStore.appliedGeographyFilter.state,
        networkOptimizationStore.appliedGeographyFilter.county,
        networkOptimizationStore.complianceScores.length,
        networkOptimizationStore.downloadingItems[DOWNLOAD_COMPLIANCE_SUMMARY_ITEM],
        specialtyLoading,
        handleExportSummary
    ]);

    if (!rosterId)
        return <Navigate to="/network-optimization/my-files" replace />;

    const tabs = useMemo(() => [
        {
            label: 'Summary',
            content: <SummaryView
                specialtyLoading={specialtyLoading}
                specialtyError={specialtyError}
                exportButton={summaryExportButton}
            />,
        },
        {
            label: 'Results',
            content: <ResultsView
                handleSetSaveRosterModalOpen={handleSetSaveRosterModalOpen}
                specialtyLoading={specialtyLoading}
                specialtyError={specialtyError}
                mapDataLoading={mapDataLoading}
                mapDataError={mapDataError}
            />,
            isDisabled: networkOptimizationStore.appliedGeographyFilter.state === '' || networkOptimizationStore.appliedGeographyFilter.county === '',
            disabledTooltipText: 'Please select a state and county'
        },
        {
            label: 'Explore Additional Providers',
            content: <ExploreView
                handleSetSaveRosterModalOpen={handleSetSaveRosterModalOpen}
                specialtyLoading={specialtyLoading}
                specialtyError={specialtyError}
                mapDataLoading={mapDataLoading}
                mapDataError={mapDataError}
            />,
            isDisabled: networkOptimizationStore.appliedGeographyFilter.state === '' || networkOptimizationStore.appliedGeographyFilter.county === '',
            disabledTooltipText: 'Please select a state and county'
        },
        {
            label: 'Disruption Analysis',
            content: <DisruptionAnalysis handleSetSaveRosterModalOpen={handleSetSaveRosterModalOpen} />,
            hideTab: !SHOW_DISRUPTION_ANALYSIS_PAGES,
        },
    ], [
        handleSetSaveRosterModalOpen,
        specialtyLoading,
        specialtyError,
        mapDataLoading,
        mapDataError,
        networkOptimizationStore.appliedGeographyFilter.state,
        networkOptimizationStore.appliedGeographyFilter.county,
        summaryExportButton
    ]);
    
    return <div className="network-analysis-page pt-5 bg-white">
        <h1 className="m-0 ml-12 mb-1 text-[28px] font-normal font-inherit">Network Analysis</h1>
        <div className="relative">
            <div className="m-0 ml-12 mb-1 text-[14px]">
                {networkOptimizationStore.rosterFileName ?
                    <div className="italic mb-4">{networkOptimizationStore.rosterFileName}</div> :
                    <SkeletonPlaceholder className='w-48 h-5 mb-4' />
                }
                <RevisionIdSelector rosterId={rosterId}/>
                {successNotification && (
                    <InlineNotification
                        kind="success"
                        subtitle="Use the dropdown to switch between the original and revised version(s) of this roster anytime."
                        title="New roster created"
                        onCloseButtonClick={() => setSuccessNotification(false)}
                        hideCloseButton={false}
                        className='inline-notification'
                    />
                )}
            </div>
            {networkOptimizationStore.revisionId > 0 && <div className="revert-revision">
                <Button kind="tertiary" renderIcon={ResetAlt} onClick={() => networkOptimizationStore.showRevertModal = true}>Revert to Original</Button>
            </div>}
        </div>         
        <Tabs tabs={tabs.filter(tab => !tab.hideTab)} onClick={(i) => networkOptimizationStore.activeTab = i}/>
        <Modal open={networkOptimizationStore.showRevertModal} size="sm" modalHeading="Revert File to Original Version"
            secondaryButtonText="Cancel" primaryButtonText="Revert to Original"
            onRequestClose={() => networkOptimizationStore.showRevertModal = false}
            onSecondarySubmit={() => networkOptimizationStore.showRevertModal = false}
            onRequestSubmit={revertToOriginal}>
            <p>Unsaved changes have been made to this revised version of the current file/roster. Would you like to
                discard these changes and revert to the original file/roster?</p>
            {loading && <div className="w-full flex justify-end">
                <Spinner />
            </div>}
        </Modal>
        
        {/* Save New Roster Modal */}
        <Modal
            open={saveRosterModalOpen}
            onRequestClose={() => setSaveRosterModalOpen(false)}
            modalHeading="Save New Roster"
            primaryButtonText="Save"
            secondaryButtonText="Cancel"
            onRequestSubmit={handleSaveNewRoster}
            primaryButtonDisabled={!fileName.trim()}
            size="sm"
        >
            <div className="mb-4">
                <TextInput
                    id="fileName"
                    labelText="File Name"
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    placeholder="Enter file name"
                />
            </div>
            <div className="mb-4">
                <TextArea
                    id="note"
                    labelText="Note (Optional)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add a note for this roster revision"
                    rows={3}
                />
            </div>
        </Modal>
        
        {/* Loading Modal */}
        <LoadingModal open={networkOptimizationStore.isRosterActionLoading} />

        {/* Recalculation Modal */}
        <Modal open={networkOptimizationStore.loadingRevisionRecalculation} modalLabel="Processing your data" passiveModal preventCloseOnClickOutside>
            <div className='w-full p-4 flex space-x-8 items-center'>
                <Spinner />
                <div>
                    <p>Thank you for your patience! Recalculating your network adequacy may take a few minutes. <br />
                        <strong>Please avoid refreshing your browser.</strong></p>
                </div>
            </div>
        </Modal>

        {/* Toast Notification */}
        {networkOptimizationStore.showToast && (
            <ToastNotification
                aria-label="closes notification"
                caption=""
                kind={networkOptimizationStore.toastKind}
                onClose={() => networkOptimizationStore.hideToastNotification()}
                onCloseButtonClick={() => networkOptimizationStore.hideToastNotification()}
                role="alert"
                statusIconDescription="notification"
                subtitle={networkOptimizationStore.toastSubtitle}
                timeout={5000}
                title={networkOptimizationStore.toastTitle}
                style={{
                    position: 'fixed',
                    top: '3rem',
                    right: '1rem',
                    zIndex: 9000,
                    minWidth: '288px',
                    maxWidth: '368px'
                }}
            />
        )}
    </div>;
}

export default observer(NetworkAnalysisPage);


